<template>
  <b-modal id="modalAufgabeDetails" @hide="onHide" size="xxl" @show="onShow" hide-footer centered>
    <template #modal-title>
      <div class="d-flex align-items-center">
        <span v-if="!isAufgabeUpdating" class="d-flex align-items-center mr-2">
          <ChevronLeft
            :size="20"
            :class="['navigationIcons', { navigationIconsDisabled: navigationDisabled }]"
            @click="navigateRow('up')"
          />
          <ChevronRight
            :size="20"
            :class="['navigationIcons', { navigationIconsDisabled: navigationDisabled }]"
            @click="navigateRow('down')"
          />
        </span>
        <b-spinner class="mr-2" small v-else></b-spinner>
        <a :href="`https://manager.skr.de/#/aufgaben/overview?aufgabenId=${aufgabeData?.id}`" target="_blank">
          <h5 class="p-0 m-0 titleLink">Aufgabe {{ aufgabeData?.id }}</h5>
        </a>
        <button
          type="button"
          @click="clickBeobachterButton"
          class="pill-badge pill-badge--button"
          style="gap: 4px; margin-left: 32px"
          :style="beobachterPillStyle"
          :id="'aufgabe-beobachter-' + aufgabeData?.id"
        >
          <Eye :size="14" />
          <span style="font-weight: 600; font-size: 14px">
            {{ aufgabeData?.beobachter.length }}
          </span>
        </button>
        <b-tooltip
          v-if="aufgabeData?.beobachter.length > 0"
          :target="'aufgabe-beobachter-' + aufgabeData?.id"
          class="p-2"
        >
          <span style="display: block; text-align: left; font-weight: 600; margin-bottom: 4px">
            Beobachter:
          </span>

          <ul style="margin: 0; padding-left: 16px">
            <li
              style="text-align: left; line-height: 20px"
              v-for="beobachter in aufgabeData?.beobachter"
              :key="'beobachter-aufgabe-' + aufgabeData?.id + '-' + beobachter"
            >
              {{ getUserName(beobachter) }}
            </li>
          </ul>
        </b-tooltip>
        <button
          v-if="aufgabeData?.autor === currentUserId"
          type="button"
          @click="clickDeleteAufgabe"
          class="deleteIconButton"
          :id="'aufgabe-loeschen-' + aufgabeData?.id"
        >
          <Trash2 :size="18" />
        </button>
        <b-tooltip
          v-if="aufgabeData?.autor === currentUserId"
          :target="'aufgabe-loeschen-' + aufgabeData?.id"
        >
          Löschen
        </b-tooltip>
      </div>
    </template>

    <template #modal-header-close>
      <X :size="20" />
    </template>

    <b-overlay :show="isAufgabeUpdating" :opacity="0.5">
      <div class="aufgabenContainer">
        <!-- Kopfbereich mit editierbaren Feldern links und Info-Panel rechts -->
        <div class="topSection">
          <div class="fieldsColumn">
            <div class="d-flex" style="gap: 8px">
              <div class="fieldGroup" style="flex: 1">
                <div class="fieldLabel">
                  <UserRound :size="16" />
                  <span>Bearbeiter/in</span>
                </div>
                <div class="d-flex" style="gap: 8px">
                  <AppSelect
                    v-model="aufgabenDetailsData.bearbeiter"
                    :options="zugewiesenePersonOptions"
                    :disabled="aufgabeData?.status === 'Erledigt'"
                    :class="{ 'feld-geaendert': hasZugewiesenePersonChanged }"
                    label="Bearbeiter/in"
                    placeholder="Suchen..."
                    no-results-text="Bearbeiter/in nicht gefunden"
                    class="feld-select"
                    @change="onBearbeiterChange"
                  />
                  <b-button
                    v-if="aufgabeData?.status !== 'Erledigt'"
                    @click="onClickIch"
                    style="height: 38px; flex-shrink: 0"
                    class="dialog-btn-secondary"
                    size="sm"
                    :disabled="aufgabenDetailsData.bearbeiter === currentUserId"
                  >
                    Ich
                  </b-button>
                </div>
              </div>
              <div class="fieldGroup" style="flex: 1">
                <div class="fieldLabel">
                  <Users :size="16" />
                  <span>Team</span>
                </div>
                <AppSelect
                  v-model="aufgabenDetailsData.team"
                  :options="teamOptions"
                  :disabled="aufgabeData?.status === 'Erledigt'"
                  :class="{ 'feld-geaendert': hasTeamChanged }"
                  label="Team"
                  placeholder="Suchen..."
                  hide-search
                  class="feld-select"
                  @change="onTeamChange"
                />
              </div>
            </div>
            <div class="fieldGroup">
              <div class="fieldLabel">
                <CircleAlert :size="16" />
                <span>Priorität</span>
              </div>
              <AppSelect
                v-model="aufgabenDetailsData.prioritaet"
                :options="prioritaetOptions"
                :disabled="aufgabeData?.status === 'Erledigt'"
                :class="{ 'feld-geaendert': hasPrioritaetChanged }"
                label="Priorität"
                placeholder="Wähle eine Priorität"
                hide-search
                class="feld-select"
                @change="saveAufgabe"
              />
            </div>
            <div class="fieldGroup">
              <div class="fieldLabel">
                <Calendar :size="16" />
                <span>Fälligkeitsdatum</span>
              </div>
              <AppDatePicker
                id="date-picker"
                v-model="aufgabenDetailsData.faelligkeitsdatum"
                value-type="YYYY-MM-DD"
                class="feld-datepicker"
                :class="{ 'feld-geaendert': hasFaelligkeitsdatumChanged }"
                :disabled="aufgabeData?.status === 'Erledigt'"
                :min="minFaelligkeitsdatum"
                placeholder="Kein Datum ausgewählt"
                :disabled-date="dateDisabled"
                @input="saveAufgabe"
              />
            </div>
          </div>

          <div class="infoPanel">
            <div class="d-flex flex-wrap triggerRow" style="gap: 12px">
              <div v-for="trigger in visibleTriggers" :key="trigger.trigger">
                <button
                  type="button"
                  class="pill-badge pill-badge--button triggerButton"
                  :style="triggerPillStyle(trigger)"
                  @click="clickTriggerButton(trigger.trigger, aufgabeData?.kategorie)"
                >
                  {{
                    trigger.trigger === 'TriggerErledigt' && isErgebnisFesthalten(aufgabeData?.kategorie)
                      ? 'Ergebnis festhalten und erledigen'
                      : triggerLabel(trigger.trigger)
                  }}
                </button>
              </div>
            </div>
            <div class="infoGrid">
              <div class="infoCell">
                <div class="infoLabel">
                  <Tag :size="16" />
                  <span>Kategorie</span>
                </div>
                <div class="infoValue">{{ getKategorieName(aufgabenDetailsData.kategorie) }}</div>
              </div>
              <div class="infoCell">
                <div class="infoLabel">
                  <Calendar :size="16" />
                  <span>Erstellungsdatum</span>
                </div>
                <div class="infoValue">{{ formatDate(aufgabeData?.createdDateUtc) }}</div>
              </div>
              <div class="infoCell">
                <div class="infoLabel">
                  <User :size="16" />
                  <span>Autor/in</span>
                </div>
                <div class="infoValue">
                  {{
                    zugewiesenePersonOptions.find(person => person.value === aufgabeData?.autor)?.text ??
                    'Automatisch erstellt'
                  }}
                </div>
              </div>
              <div class="infoCell">
                <div class="infoLabel">
                  <Calendar :size="16" />
                  <span>Startdatum</span>
                </div>
                <div class="infoValue">{{ formatDate(aufgabeData?.statusInArbeitDatum) || '-' }}</div>
              </div>
              <div class="infoCell">
                <div class="infoLabel">
                  <ListChecks :size="16" />
                  <span>Status</span>
                </div>
                <div class="infoValue"><span v-html="statusHtml"></span></div>
              </div>
              <div class="infoCell">
                <div class="infoLabel">
                  <Calendar :size="16" />
                  <span>Beendigungsdatum</span>
                </div>
                <div class="infoValue">{{ formatDate(aufgabeData?.statusErledigtDatum) || '-' }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Beschreibung -->
        <div class="contentSection">
          <div class="fieldLabel mb-2">
            <Pencil :size="16" />
            <span>Beschreibung</span>
          </div>
          <div>
            <Editor
              v-model="aufgabenDetailsData.beschreibung"
              placeholder="Schreibe eine Beschreibung"
              :isDisabled="aufgabeData?.status === 'Erledigt'"
              :disableMentions="true"
              @imagePaste="onImagePaste"
            />
            <!-- Speichern-Button unter dem Beschreibungsfeld, ausgegraut wenn keine Änderung vorliegt -->
            <div v-if="aufgabeData?.status !== 'Erledigt'" class="d-flex justify-content-end mt-3">
              <b-button
                variant="primary"
                size="sm"
                class="beschreibungSaveButton dialog-btn"
                :disabled="!hasBeschreibungChanged"
                @click="saveAufgabe"
              >
                Speichern
              </b-button>
            </div>
          </div>
        </div>

        <div class="contentSection">
          <div class="d-flex align-items-center justify-content-between">
            <div class="tabs">
              <button
                v-for="tab in visibleTabs"
                type="button"
                class="tab"
                :class="{ active: activeTab === tab.text }"
                @click="activeTab = tab.text"
                :key="tab.text"
              >
                {{ tab.text }}
              </button>
            </div>
            <b-button
              v-if="activeTab === 'Verlinkungen'"
              variant="link"
              class="verlinkungen-bearbeiten-btn p-0"
              @click="showVerlinkungenBearbeiten = true"
            >
              <Pencil :size="16" class="mr-1" />
              Bearbeiten
            </b-button>
          </div>

          <div v-if="activeTab === 'Verlinkungen'">
            <div v-if="isVerlinkungenLoading" class="d-flex justify-content-center align-items-center py-6">
              <b-spinner></b-spinner>
            </div>
            <div v-else-if="!verlinkungExists" class="emptyBox">Keine Verlinkungen vorhanden</div>
            <div v-else>
              <!-- REISETERMIN VERLINKUNGEN -->
              <div class="mt-4" v-if="aufgabeData?.reiseTermine?.length > 0">
                <h6>
                  Reisetermine
                  {{
                    aufgabeData?.reiseTermine?.length > 1 ? '(' + aufgabeData?.reiseTermine?.length + ')' : ''
                  }}
                </h6>

                <AufgabeAttachmentReiseterminTable
                  :reiseterminData="aufgabeData?.reiseTermine"
                  :aufgabenKategorie="aufgabeData?.kategorie"
                  :isRepruAufgabe="isRepruAufgabe"
                  :openAufgabenId="aufgabeData.id"
                />
              </div>

              <!-- FVC VERLINKUNGEN -->
              <div
                class="mt-4"
                v-if="aufgabeData?.flugverfuegbarkeiten?.length > 0 && !isFlugeinkaufKategorie"
              >
                <h6>FVC</h6>
                <FVCAttachmentTable
                  :fvcData="aufgabeData.flugverfuegbarkeiten"
                  :openAufgabenId="aufgabeData.id"
                  :hideFlugstammdatenButton="aufgabeData?.reiseTermine?.length > 0"
                />
              </div>

              <!-- FLUGEINKAUF VERLINKUNGEN -->
              <div class="mt-4" v-if="flugeinkaufFraData.length > 0 && isFlugeinkaufKategorie">
                <h6>Flugeinkauf</h6>
                <FlugeinkaufAttachmentTable
                  :flugeinkaufData="flugeinkaufFraData"
                  :openAufgabenId="aufgabeData.id"
                />
              </div>

              <!-- REISE VERLINKUNGEN -->
              <div class="mt-4" v-if="aufgabeData?.reisen?.length > 0">
                <h6>
                  Reisen {{ aufgabeData?.reisen?.length > 1 ? '(' + aufgabeData?.reisen?.length + ')' : '' }}
                </h6>
                <ReisenAttachmentTable
                  :reisenData="aufgabeData?.reisen"
                  :openAufgabenId="aufgabeData.id"
                  :aufgabeKategorie="aufgabeData?.kategorie"
                />
              </div>

              <!-- VORGANG VERLINKUNGEN -->
              <div class="mt-4" v-if="aufgabeData?.vorgaenge?.length > 0">
                <h6>
                  Vorgänge
                  {{ aufgabeData?.vorgaenge?.length > 1 ? '(' + aufgabeData?.vorgaenge?.length + ')' : '' }}
                </h6>
                <VorgangAttachmentTable
                  :vorgangData="aufgabeData.vorgaenge"
                  :openAufgabenId="aufgabeData.id"
                  :gueltigeKonfiguration="gueltigeKonfiguration"
                  :aufgabenKategorie="aufgabeData?.kategorie"
                  :isRepruAufgabe="isRepruAufgabe"
                  :reiseTermine="aufgabeData?.reiseTermine"
                />
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'Leistungen'">
            <div v-if="isLeistungenLoading" class="d-flex justify-content-center align-items-center py-10">
              <b-spinner></b-spinner>
            </div>
            <AufgabenLeistungen v-else></AufgabenLeistungen>
          </div>

          <div v-if="activeTab === 'Flugdaten'">
            <div v-if="isVerlinkungenLoading" class="d-flex justify-content-center align-items-center py-10">
              <b-spinner></b-spinner>
            </div>
            <AufgabenZusaetzlicheInfos
              v-else
              :showFlugdetails="showFlugdetails"
              :reisetermine="aufgabeData?.reiseTermine"
            />
          </div>

          <div v-if="activeTab === 'Dateien'">
            <AufgabenDateien
              :aufgabe-id="aufgabeData?.id"
              :dateien="aufgabeData?.dateien"
              @deleted="onDateiDeleted"
              @uploaded="onDateiUploaded"
            />
          </div>
        </div>

        <!-- Unterer Tab-Bereich mit Kommentaren und Verlauf -->
        <div class="contentSection">
          <div class="tabs">
            <button
              v-for="tab in bottomTabs"
              type="button"
              class="tab"
              :class="{ active: activeBottomTab === tab.text }"
              @click="activeBottomTab = tab.text"
              :key="tab.text"
            >
              {{ tab.text }}
            </button>
          </div>
          <div v-if="activeBottomTab === 'Kommentare'" class="commentsWrapper">
            <AufgabenComments
              :aufgabeId="aufgabeData?.id"
              :comments="getCommentsWithMappedAnswers"
              @updateComments="onUpdateComments"
            />
          </div>
          <div v-if="activeBottomTab === 'Verlauf'">
            <AufgabenVerlauf
              v-if="hasVerlauf"
              :data="verlaufData"
              :erstellungsdatum="aufgabeData?.createdDateUtc"
            />
            <div v-else class="emptyBox">Kein Verlauf vorhanden</div>
          </div>
        </div>

        <!-- Ergebnis-Bereich, sofern ein Ergebnis vorhanden ist -->
        <div class="contentSection" v-if="aufgabeData?.ergebnis">
          <div class="infoGrid">
            <div class="infoCell">
              <div class="infoLabel">
                <ListChecks :size="16" />
                <span>Ergebnis</span>
              </div>
              <div class="infoValue">{{ getErgebnisText }}</div>
            </div>
            <div class="infoCell" v-if="aufgabeData?.ergebnisBeschreibung">
              <div class="infoLabel">
                <Pencil :size="16" />
                <span>Beschreibung</span>
              </div>
              <div class="infoValue">{{ aufgabeData?.ergebnisBeschreibung ?? '-' }}</div>
            </div>
          </div>
        </div>

        <AufgabeErgebnisFesthaltenModal
          :aufgabeId="aufgabeData?.id ?? 0"
          :aufgabenKategorie="aufgabeData?.kategorie ?? ''"
          @ergebnisGespeichert="onErgebnisGespeichert"
        />

        <VerlinkungenBearbeitenModal
          v-if="showVerlinkungenBearbeiten"
          :aufgabe-id="aufgabeData?.id"
          :initial-verlinkungen="bestehendeVerlinkungen"
          @saved="onVerlinkungenSaved"
          @close="showVerlinkungenBearbeiten = false"
        />

        <DeleteConfirmationModal
          id="modalAufgabeLoeschen"
          title="Aufgabe löschen?"
          entityBezeichnung="die Aufgabe"
          @confirm="confirmDeleteAufgabe"
        />
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import apiService from '@/core/common/services/api.service';
import AppSelect from '@/components/common/app-select/app-select.vue';
import AppDatePicker from '@/components/common/app-date-picker/app-date-picker.vue';

import AufgabeErgebnisFesthaltenModal from '@/components/aufgaben/aufgabe-ergebnis-festhalten-modal.vue';
import FVCAttachmentTable from '@/components/aufgaben/aufgabe-attachment-fvc-table.vue';
import FlugeinkaufAttachmentTable from '@/components/aufgaben/aufgabe-attachment-flugeinkauf-table.vue';
import ReisenAttachmentTable from '@/components/aufgaben/aufgabe-attachment-reise-table.vue';
import VorgangAttachmentTable from '@/components/aufgaben/aufgabe-attachment-vorgang-table.vue';
import AufgabenVerlauf from '@/components/aufgaben/aufgaben-verlauf.vue';
import AufgabenLeistungen from '@/components/aufgaben/aufgaben-leistungen.vue';
import AufgabenComments from '@/components/comments/aufgaben-comments.vue';
import VerlinkungenBearbeitenModal from '@/components/aufgaben/verlinkungen-bearbeiten-modal.vue';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';
import {
  SET_AUFGABE_DETAIL_DATA,
  EDIT_AUFGABE,
  LOAD_TRIGGERS,
  EDIT_STATUS,
  ADD_BEOBACHTER,
  DELETE_BEOBACHTER,
  SET_COMMENTS_AUFGABEN_DETAIL,
  FETCH_VERLINKUNGEN_DATA,
  FETCH_LEISTUNGEN_DATA,
} from '@/core/aufgaben/stores/aufgaben.module';

import { SET_COMMENT_TEXT, SET_MODE, SAVE_COMMENT } from '@/core/comments/comments.module';
import { mapState, mapGetters } from 'vuex';
import { isEqual } from 'date-fns';
import {
  changePeopleToCorrectShape,
  changeTeamsToCorrectShape,
  triggerLabel,
  statusFormatterHtml,
  aufgabenStatusColors,
  grauPillColors,
  formatDate,
} from '@/core/common/helpers/utils.js';
import AufgabeAttachmentReiseterminTable from '@/components/aufgaben/aufgabe-attachment-reisetermin-table.vue';
import Editor from '@/components/common/editor.vue';
import AufgabenZusaetzlicheInfos from './aufgaben-zusaetzliche-infos.vue';
import AufgabenDateien from './aufgaben-dateien.vue';
import { uploadDatei, deleteDatei, getReferenzierteBildDateinamen } from '@/core/common/helpers/datei.utils';
import { AUFGABENGRUPPE_REPRU } from '@/core/common/helpers/aufgabengruppen.js';
import { FLUGEINKAUF_KATEGORIEN } from '@/core/common/helpers/aufgaben-kategorien.js';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  UserRound,
  Users,
  CircleAlert,
  Calendar,
  Tag,
  User,
  ListChecks,
  Pencil,
  Trash2,
  X,
} from 'lucide-vue';

const beobachterAktivColors = { background: '#3699FF26', borderColor: '#3699FF', color: '#004A99' };

export default {
  name: 'AufgabeDetailsModal',
  components: {
    AppDatePicker,
    Editor,
    AppSelect,
    AufgabeAttachmentReiseterminTable,
    AufgabeErgebnisFesthaltenModal,
    FVCAttachmentTable,
    FlugeinkaufAttachmentTable,
    ReisenAttachmentTable,
    VorgangAttachmentTable,
    AufgabenVerlauf,
    AufgabenZusaetzlicheInfos,
    AufgabenComments,
    AufgabenLeistungen,
    AufgabenDateien,
    VerlinkungenBearbeitenModal,
    DeleteConfirmationModal,
    ChevronLeft,
    ChevronRight,
    Eye,
    UserRound,
    Users,
    CircleAlert,
    Calendar,
    Tag,
    User,
    ListChecks,
    Pencil,
    Trash2,
    X,
  },
  props: {
    navigationDisabled: { type: Boolean, default: false },
  },
  data() {
    return {
      triggers: [],
      prioritaetOptions: [
        { value: 0, text: 'Niedrig' },
        { value: 1, text: 'Mittel' },
        { value: 2, text: 'Hoch' },
      ],
      aufgabenDetailsData: {
        beschreibung: null,
        faelligkeitsdatum: null,
        bearbeiter: null,
        prioritaet: null,
        kategorie: null,
        team: null,
      },
      activeTab: 'Verlinkungen',
      activeBottomTab: 'Kommentare',
      showVerlinkungenBearbeiten: false,
      // Dateinamen der während der aktuellen Bearbeitung neu hochgeladenen Bild-Anhänge
      // Fängt den Fall ab, dass ein Bild eingefügt, wieder gelöscht und gespeichert wird, ohne je
      // in einer gespeicherten Beschreibung gestanden zu haben
      neuHochgeladeneBildDateinamen: new Set(),
      // Trackt welche Tabs bereits geladen wurden
      loadedTabs: {},
      currentAufgabeId: null,
      verlaufData: null,
      statusFormatterHtml,
    };
  },
  watch: {
    // Reagiert nur auf neue Aufgaben
    aufgabeData(newAufgabeData) {
      if (newAufgabeData != null && newAufgabeData.id !== this.currentAufgabeId) {
        this.currentAufgabeId = newAufgabeData.id;
        this.loadTriggers();
        this.loadVerlauf(this.aufgabeData?.id);
        this.loadedTabs = {};
        this.initializeData();
        this.$nextTick(() => {
          this.validateActiveTab();
          this.loadActiveTabData();
        });
      }
    },
    // Bei Tab-Wechsel die Daten für den neuen Tab laden
    activeTab() {
      this.loadActiveTabData();
    },
    // Beim Wechsel auf den Verlauf-Tab wird dieser jedes Mal neu geladen,
    activeBottomTab(tab) {
      if (tab === 'Verlauf') {
        this.loadVerlauf(this.aufgabeData?.id);
      }
    },
  },
  computed: {
    ...mapGetters([
      'currentUserId',
      'getKategorienWithErgebnisFesthalten',
      'getKategorienLabel',
      'getAllKategorien',
    ]),
    ...mapState({
      aufgabeData: state => state.aufgaben.aufgabeDetail,
      isAufgabeUpdating: state => state.aufgaben.isAufgabeUpdating,
      // Loading-States für die Lazy-Loading Spinner in den Tabs
      isVerlinkungenLoading: state => state.aufgaben.isVerlinkungenLoading,
      isLeistungenLoading: state => state.aufgaben.isLeistungenLoading,
      users: state => state.users['users'],
      kategorienConfig: state => state.stateConfig.kategorienConfig,
    }),
    getCommentsWithMappedAnswers() {
      if (!this.aufgabeData || !this.aufgabeData.comments?.length > 0) {
        return [];
      }

      const answers = this.aufgabeData.comments.filter(comment => comment.parentId !== null);
      const commentsWithAnswers = this.aufgabeData.comments
        .filter(comment => comment.parentId === null)
        .map(comment => {
          const matchingAnswers = answers.filter(answer => answer.parentId === comment.id);
          if (matchingAnswers.length > 0) {
            return { ...comment, answers: matchingAnswers };
          } else return { ...comment, answers: [] };
        })
        .sort((a, b) => new Date(b.createdDateUtc) - new Date(a.createdDateUtc));
      return commentsWithAnswers;
    },
    tabs() {
      return [
        { show: true, text: 'Verlinkungen' },
        { show: this.showFlugdetails, text: 'Flugdaten' },
        { show: this.showLeistungen, text: 'Leistungen' },
        { show: true, text: 'Dateien' },
      ];
    },
    visibleTabs() {
      return this.tabs.filter(tab => tab.show);
    },
    bottomTabs() {
      return [{ text: 'Kommentare' }, { text: 'Verlauf' }];
    },
    hasVerlauf() {
      return this.verlaufData != null && Object.keys(this.verlaufData).length > 0;
    },
    visibleTriggers() {
      const currentStatusTrigger = 'Trigger' + this.aufgabeData?.status;
      return this.triggers.filter(
        t =>
          t.trigger !== currentStatusTrigger &&
          (t.allowed ||
            (t.trigger === 'TriggerErledigt' && this.isErgebnisFesthalten(this.aufgabeData?.kategorie)))
      );
    },
    statusHtml() {
      if (this.aufgabeData) return this.statusFormatterHtml[this.aufgabeData.status];
      return '-';
    },
    beobachterPillStyle() {
      return this.buttonPillStyle(this.isActiveUserBeobachter ? beobachterAktivColors : grauPillColors);
    },
    gueltigeKonfiguration() {
      if (this.aufgabeData.reiseTermine?.length !== 1) {
        return null;
      }

      return this.aufgabeData.reiseTermine[0].gueltigeKonfiguration;
    },
    isRepruAufgabe() {
      return !!this.kategorienConfig?.[this.aufgabeData?.kategorie]?.aufgabengruppen?.includes(
        AUFGABENGRUPPE_REPRU
      );
    },
    isFlugeinkaufKategorie() {
      return FLUGEINKAUF_KATEGORIEN.includes(this.aufgabeData?.kategorie);
    },
    flugeinkaufFraData() {
      return (this.aufgabeData?.flugverfuegbarkeiten ?? []).filter(
        fv => fv.deutscherAbflughafenIataCode === 'FRA'
      );
    },
    showLeistungen() {
      return (
        (this.isRepruAufgabe && this.aufgabeData?.leistungen?.length > 0) ||
        (this.aufgabeData?.kategorie === 'Sonstiges' && this.aufgabeData?.leistungen?.length > 0)
      );
    },
    showFlugdetails() {
      return (
        this.aufgabeData?.kategorie === 'VorzeitigesTicketing' ||
        this.aufgabeData?.kategorie === 'VorabpruefungTicketBeiGruppe' ||
        this.aufgabeData?.kategorie === 'TicketfreigabeYield' ||
        this.aufgabeData?.kategorie === 'SpecialServices' ||
        this.aufgabeData?.kategorie === 'NegoQuotieren' ||
        this.aufgabeData?.kategorie === 'Individualisierung' ||
        this.aufgabeData?.kategorie === 'GruppeTerminabsageOderVerschiebung' ||
        this.aufgabeData?.kategorie === 'FlugeinkaufsartFestlegen' ||
        this.aufgabeData?.kategorie === 'TicketingAnfrage' ||
        this.aufgabeData?.kategorie === 'FlugplanaenderungAbsprache' ||
        this.aufgabeData?.kategorie === 'GruppeFlugplanung' ||
        this.aufgabeData?.kategorie === 'Flugbuchung' ||
        this.aufgabeData?.kategorie === 'Flugpruefung' ||
        this.aufgabeData?.kategorie === 'Flugplanaenderung' ||
        this.aufgabeData?.kategorie === 'InlandsflugBuchung' ||
        this.aufgabeData?.kategorie === 'InlandsflugPruefung' ||
        this.aufgabeData?.kategorie === 'InlandsflugAbsprache' ||
        this.aufgabeData?.kategorie === 'GruppeTicketing' ||
        this.aufgabeData?.kategorie === 'SegmenteMismatch' ||
        this.aufgabeData?.kategorie === 'OptionsfristErreicht' ||
        this.aufgabeData?.kategorie === 'UnverbindlichesFlugangebot'
      );
    },
    isActiveUserBeobachter() {
      return this.aufgabeData?.beobachter.includes(this.currentUserId);
    },
    teamOptions() {
      const abkuerzungen = {
        'Destination Operations Management': 'DOM',
        'Destination Success Management': 'DSM',
        'Customer Success Management': 'CSM',
        'Flight Operations Management': 'FOM',
      };
      return this.changeTeamsToCorrectShape(this.users).map(option => ({
        value: option.value,
        text: abkuerzungen[option.value] || option.text,
      }));
    },
    zugewiesenePersonOptions() {
      return changePeopleToCorrectShape(this.users);
    },
    hasFaelligkeitsdatumChanged() {
      return !isEqual(
        new Date(this.aufgabeData?.faelligkeitsdatum?.substring(0, 10)),
        new Date(this.aufgabenDetailsData.faelligkeitsdatum?.substring(0, 10))
      );
    },
    hasBeschreibungChanged() {
      const nullFormattedBeschreibung = this.aufgabenDetailsData.beschreibung ?? '';
      const nullFormattedBeschreibung2 = this.aufgabeData?.beschreibung ?? '';
      return nullFormattedBeschreibung2 != nullFormattedBeschreibung;
    },
    hasZugewiesenePersonChanged() {
      return this.aufgabeData?.bearbeiter !== this.aufgabenDetailsData.bearbeiter;
    },
    hasPrioritaetChanged() {
      return this.aufgabeData?.prioritaet !== this.aufgabenDetailsData.prioritaet;
    },
    hasTeamChanged() {
      return this.aufgabeData?.team !== this.aufgabenDetailsData.team;
    },
    minFaelligkeitsdatum() {
      return new Date();
    },
    verlinkungExists() {
      if (this.aufgabeData) {
        return (
          this.aufgabeData.reiseTermine?.length > 0 ||
          this.aufgabeData.reisen?.length > 0 ||
          this.aufgabeData.vorgaenge?.length > 0 ||
          this.aufgabeData.flugverfuegbarkeiten?.length > 0
        );
      } else return false;
    },
    // Bestehende Verlinkungen als Editor-Zeilen für das Bearbeiten-Modal
    bestehendeVerlinkungen() {
      const reiseTermine = (this.aufgabeData?.reiseTermine ?? []).map(termin => ({
        typ: 'Reisetermin',
        artifactId: termin.id,
        artifactText: termin.reiseterminkuerzel,
      }));
      const reisen = (this.aufgabeData?.reisen ?? []).map(reise => ({
        typ: 'Reise',
        artifactId: reise.id,
        artifactText: reise.reisekuerzel,
      }));
      const vorgaenge = (this.aufgabeData?.vorgaenge ?? []).map(vorgang => ({
        typ: 'Vorgang',
        artifactId: vorgang.id,
        artifactText: null,
      }));
      return [...reiseTermine, ...reisen, ...vorgaenge];
    },
    getErgebnisText() {
      if (this.aufgabeData && this.aufgabeData.ergebnis && this.getAllKategorien) {
        for (const kategorie of this.getAllKategorien) {
          if (kategorie.ergebnisKategorienOptions) {
            const ergebnisOption = kategorie.ergebnisKategorienOptions.find(
              option => option.ergebnisKategorie === this.aufgabeData.ergebnis
            );
            if (ergebnisOption) {
              return ergebnisOption.labelFrontend;
            }
          }
        }
      }
      return '';
    },
  },

  methods: {
    formatDate,
    onUpdateComments(res) {
      this.$store.commit(SET_COMMENTS_AUFGABEN_DETAIL, res.comments);
    },
    changeTeamsToCorrectShape,
    onErgebnisGespeichert() {
      this.$store
        .dispatch(EDIT_STATUS, { aufgabeId: this.aufgabeData.id, trigger: 'TriggerErledigt' })
        .then(response => {
          this.toast('Status wurde bearbeitet.');
          this.$emit('aufgabeBearbeitet', response);
          this.$bvModal.hide('aufgabeErgebnisFesthaltenModal');
          this.loadTriggers();
          this.loadVerlauf(this.aufgabeData?.id);
        });
    },
    isErgebnisFesthalten(kategorie) {
      return this.getKategorienWithErgebnisFesthalten.includes(kategorie);
    },
    triggerLabel,
    buttonPillStyle(colors) {
      return {
        '--pill-bg': colors.background,
        '--pill-bg-hover': colors.borderColor + '40',
        border: '0.5px solid ' + colors.borderColor,
        color: colors.color,
      };
    },
    triggerPillStyle(trigger) {
      const colors = aufgabenStatusColors[trigger.trigger.replace('Trigger', '')];
      return colors ? this.buttonPillStyle(colors) : {};
    },
    clickBeobachterButton() {
      const aufgabeId = this.aufgabeData?.id;
      const userId = this.currentUserId;
      const action = this.isActiveUserBeobachter ? DELETE_BEOBACHTER : ADD_BEOBACHTER;
      this.$store.dispatch(action, { id: aufgabeId, userId }).then(updatedAufgabe => {
        this.$emit('updateOverviewData', updatedAufgabe);
        this.toast('Bearbeitet');
        this.loadVerlauf(aufgabeId);
      });
    },
    clickDeleteAufgabe() {
      this.$bvModal.show('modalAufgabeLoeschen');
    },
    confirmDeleteAufgabe() {
      this.$bvModal.hide('modalAufgabeLoeschen');
      if (this.aufgabeData.id) {
        apiService.delete('/aufgaben', { id: this.aufgabeData.id }).then(() => {
          this.$router.go('/aufgaben/overview');
        });
      }
    },
    getKategorieName(kategorieEnum) {
      return this.getKategorienLabel(kategorieEnum);
    },
    loadTriggers() {
      this.$store
        .dispatch(LOAD_TRIGGERS, { aufgabeId: this.aufgabeData?.id })
        .then(res => (this.triggers = res));
    },
    onHide(bvModalEvt) {
      if (bvModalEvt.trigger === 'backdrop' || bvModalEvt.trigger === 'esc') {
        this.persistBeschreibungAndKommentar();
      }
      this.$store.commit(SET_AUFGABE_DETAIL_DATA, null);
      this.$store.commit(SET_COMMENT_TEXT, '');
      this.$store.commit(SET_MODE, '');
      this.removeKeyEventListener();
      this.activeTab = 'Verlinkungen';
      this.activeBottomTab = 'Kommentare';
      this.loadedTabs = {};
      this.currentAufgabeId = null;
    },
    persistBeschreibungAndKommentar() {
      if (this.hasBeschreibungChanged && this.aufgabeData?.id) {
        this.saveAufgabe();
        this.toast('Aufgabe wurde bearbeitet');
      }
      const commentText = this.$store.state.comments.commentText;
      if (commentText && commentText.trim() !== '' && this.aufgabeData?.id) {
        this.$store.dispatch(SAVE_COMMENT, { isAufgabenComments: true, aufgabeId: this.aufgabeData.id });
        this.toast('Kommentar wurde gespeichert');
      }
    },
    onShow() {
      this.loadedTabs = {};
      this.initializeData();
      this.loadVerlauf(this.aufgabeData?.id);
      this.loadActiveTabData();
      this.addKeyEventListener();
    },
    initializeData() {
      this.neuHochgeladeneBildDateinamen = new Set();
      this.aufgabenDetailsData.beschreibung = this.aufgabeData?.beschreibung;
      this.aufgabenDetailsData.faelligkeitsdatum = this.aufgabeData?.faelligkeitsdatum;
      this.aufgabenDetailsData.bearbeiter = this.aufgabeData?.bearbeiter;
      this.aufgabenDetailsData.prioritaet = this.aufgabeData?.prioritaet;
      this.aufgabenDetailsData.kategorie = this.aufgabeData?.kategorie;
      this.aufgabenDetailsData.team = this.aufgabeData?.team;
      // Bei Repru-Aufgaben mit Leistungen direkt den Leistungen-Tab als Default anzeigen
      if (
        (this.isRepruAufgabe && this.aufgabeData?.leistungen?.length > 0) ||
        (this.aufgabeData?.kategorie === 'Sonstiges' && this.aufgabeData?.leistungen?.length > 0)
      ) {
        this.activeTab = 'Leistungen';
      }
    },
    async saveAufgabe() {
      // Vor dem Speichern ermitteln, welche Bild-Anhänge aus der Beschreibung entfernt wurden
      const zuLoeschendeBildAnhaenge = this.ermittleEntfernteBildAnhaenge();
      const changedFields = {
        beschreibung: this.hasBeschreibungChanged
          ? this.aufgabenDetailsData.beschreibung
          : this.aufgabeData.beschreibung,
        faelligkeitsdatum: this.hasFaelligkeitsdatumChanged
          ? this.aufgabenDetailsData.faelligkeitsdatum
          : this.aufgabeData.faelligkeitsdatum,
        bearbeiter: this.hasZugewiesenePersonChanged
          ? this.aufgabenDetailsData.bearbeiter
          : this.aufgabeData.bearbeiter,
        prioritaet: this.hasPrioritaetChanged
          ? this.aufgabenDetailsData.prioritaet
          : this.aufgabeData.prioritaet,
        team: this.hasTeamChanged ? this.aufgabenDetailsData.team : this.aufgabeData.team,
        id: this.aufgabeData.id,
      };
      // zugewiesener user darf alles ändern
      this.$store.dispatch(EDIT_AUFGABE, changedFields).then(async updatedAufgabe => {
        this.$emit('aufgabeBearbeitet', updatedAufgabe);
        this.toast('Aufgabe wurde bearbeitet.');
        // Team-/Bearbeiterwechsel kann den Status ändern (z.B. InArbeit -> Offen), daher Trigger neu laden
        this.loadTriggers();
        await this.loescheBildAnhaenge(zuLoeschendeBildAnhaenge);
        this.loadVerlauf(this.aufgabeData?.id);
      });
    },
    navigateRow(direction) {
      // Bei deaktivierter Navigation wird das Blättern unterbunden
      if (this.navigationDisabled) {
        return;
      }
      const isEditorActive = Array.from(document.getElementsByClassName('tiptap')).some(
        item => document.activeElement === item
      );
      if (!isEditorActive && !this.isAufgabeUpdating) {
        this.$emit('rowSelectionChanged', direction);
      }
    },
    handleKeyPress($event) {
      const isBeschreibungActive = document.activeElement === document.getElementById('input-beschreibung');
      if (!isBeschreibungActive) {
        if ($event.key === 'ArrowLeft') {
          this.navigateRow('up');
        } else if ($event.key === 'ArrowRight') {
          this.navigateRow('down');
        }
      }
    },
    addKeyEventListener() {
      window.addEventListener('keydown', this.handleKeyPress);
    },
    removeKeyEventListener() {
      window.removeEventListener('keydown', this.handleKeyPress);
    },
    clickTriggerButton(trigger, kategorie) {
      if (trigger === 'TriggerErledigt' && this.isErgebnisFesthalten(kategorie)) {
        this.$bvModal.show('aufgabeErgebnisFesthaltenModal');
      } else {
        this.$store.dispatch(EDIT_STATUS, { aufgabeId: this.aufgabeData.id, trigger }).then(response => {
          this.aufgabenDetailsData.bearbeiter = response?.bearbeiter ?? null;
          this.aufgabenDetailsData.team = response?.team ?? null;
          this.toast('Status wurde bearbeitet.');
          this.$emit('aufgabeBearbeitet', response);
          this.loadTriggers();
          this.loadVerlauf(this.aufgabeData?.id);
        });
      }
    },
    getUserName(userId) {
      const user = this.users.find(user => user.id === userId);
      if (!user) return '';
      return user.givenName + ' ' + user.surname;
    },
    onBearbeiterChange() {
      this.aufgabenDetailsData.team = null;
      this.saveAufgabe();
    },
    onClickIch() {
      this.aufgabenDetailsData.bearbeiter = this.currentUserId;
      this.onBearbeiterChange();
    },
    onTeamChange() {
      this.aufgabenDetailsData.bearbeiter = null;
      this.saveAufgabe();
    },
    // Lazy Loading: Lädt die Daten für den aktuell aktiven Tab, falls noch nicht geschehen.
    loadActiveTabData() {
      if (!this.aufgabeData?.id) return;
      const aufgabenId = this.aufgabeData.id;

      if (
        (this.activeTab === 'Verlinkungen' || this.activeTab === 'Flugdaten') &&
        !this.loadedTabs.verlinkungen
      ) {
        this.loadedTabs = { ...this.loadedTabs, verlinkungen: true };
        this.$store.dispatch(FETCH_VERLINKUNGEN_DATA, { aufgabenId });
      }

      if (this.activeTab === 'Leistungen' && !this.loadedTabs.leistungen) {
        this.loadedTabs = { ...this.loadedTabs, leistungen: true };
        this.$store.dispatch(FETCH_LEISTUNGEN_DATA, { aufgabenId });
      }
    },
    loadVerlauf(aufgabenId) {
      if (aufgabenId) {
        apiService.get('/history/aufgaben/' + aufgabenId).then(response => {
          if (response.status === 200) {
            this.verlaufData = response.data.result;
          }
        });
      }
    },
    onVerlinkungenSaved() {
      const aufgabenId = this.aufgabeData?.id;
      // Lädt die Verlinkungs-Daten neu
      this.$store.dispatch(FETCH_VERLINKUNGEN_DATA, { aufgabenId });
      this.loadVerlauf(aufgabenId);
    },
    dateDisabled(date) {
      const weekday = date.getDay();
      return weekday === 0 || weekday === 6;
    },
    validateActiveTab() {
      const availableTabs = this.tabs.filter(tab => tab.show).map(tab => tab.text);
      if (!availableTabs.includes(this.activeTab)) {
        this.activeTab = 'Verlinkungen';
      }
    },
    // schreibt die aktualisierte Dateien-Liste in den vuex Store und lädt den Verlauf neu
    updateDateien(dateien) {
      this.$store.commit(SET_AUFGABE_DETAIL_DATA, { ...this.aufgabeData, dateien });
      this.loadVerlauf(this.aufgabeData?.id);
    },
    // aktualisiert den vuex Store nachdem eine datei gelöscht wurde
    onDateiDeleted(dateiId) {
      this.updateDateien(this.aufgabeData.dateien.filter(datei => datei.id !== dateiId));
    },
    // Ermittelt eingefügte Bild-Anhänge, die nicht mehr in der Beschreibung vorkommen
    ermittleEntfernteBildAnhaenge() {
      const dateien = this.aufgabeData?.dateien || [];
      if (!dateien.length) return [];
      const aktuelleNamen = getReferenzierteBildDateinamen(this.aufgabenDetailsData.beschreibung);
      const vorherigeNamen = getReferenzierteBildDateinamen(this.aufgabeData.beschreibung);
      return dateien.filter(
        datei =>
          /^Bild_/.test(datei.dateiname) &&
          !aktuelleNamen.has(datei.dateiname) &&
          (vorherigeNamen.has(datei.dateiname) || this.neuHochgeladeneBildDateinamen.has(datei.dateiname))
      );
    },
    // Löscht die übergebenen Bild-Anhänge im Backend und aktualisiert die Dateien-Liste im Store
    async loescheBildAnhaenge(zuLoeschen) {
      if (!zuLoeschen?.length) return;
      await Promise.all(zuLoeschen.map(datei => deleteDatei(datei.id)));
      const geloeschteIds = new Set(zuLoeschen.map(datei => datei.id));
      const verbleibende = (this.aufgabeData?.dateien || []).filter(datei => !geloeschteIds.has(datei.id));
      this.updateDateien(verbleibende);
    },
    // aktualisiert den vuex Store nachdem eine datei hochgeladen wurde
    onDateiUploaded(neueDatei) {
      this.updateDateien([...(this.aufgabeData.dateien || []), neueDatei]);
    },
    // Lädt ein aus der Zwischenablage eingefügtes Bild direkt als Anhang der geöffneten Aufgabe hoch
    // Im Gegensatz zum Erstellen-Modal gibt es hier keine lokale Datei-Liste, da die Aufgabe bereits existiert
    async onImagePaste(file) {
      if (!this.aufgabeData?.id) return;
      const response = await uploadDatei(this.aufgabeData.id, file);
      this.neuHochgeladeneBildDateinamen.add(file.name);
      // Aktualisiert den Vuex-Store, damit die neue Datei sofort im Dateien-Tab sichtbar ist
      this.onDateiUploaded(response.data.result);
      this.toast('Bild wurde als Anhang hinzugefügt.');
    },
  },
};
</script>
<style scoped>
.editField {
  border: 1px solid rgba(128, 128, 128, 0.2);
}
.editField:focus {
  outline: none !important;
}

.editField:hover {
  border: 1px solid rgba(128, 128, 128, 0.544);
  border-radius: 8px;
}
.changedField {
  border: 1px solid rgba(59, 85, 180, 0.544) !important;
  outline: none;
  border-radius: 8px;
}

.greyedOut {
  background-color: rgba(114, 114, 114, 0.05) !important;
  border: none;
}

.navigationIcons {
  color: #004a99;
  cursor: pointer;
}

.navigationIcons:hover {
  color: var(--primary);
}

.navigationIconsDisabled {
  color: #c4c4c4;
  cursor: default;
  pointer-events: none;
}

.titleLink {
  color: #004a99;
}

/* Kopfbereich mit Feldern links und Info-Panel rechts */
.topSection {
  display: flex;
  align-items: stretch;
}

.fieldsColumn {
  flex: 1 1 50%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 32px;
  min-width: 450px;
}

.fieldGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fieldLabel {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Poppins, Helvetica, 'sans-serif';
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0;
  color: var(--text);
}

.fieldLabel svg {
  color: var(--primary);
}

.feld-select,
.feld-datepicker {
  --app-select-shadow: none;
  --app-date-picker-shadow: none;
}

/* markiert ein Feld, dessen Wert vom ursprünglich geladenen Stand abweicht */
.feld-geaendert {
  --app-select-border-color: rgba(59, 85, 180, 0.544);
  --app-date-picker-border-color: rgba(59, 85, 180, 0.544);
}

/* Rechtes Info-Panel mit grauem Hintergrund */
.infoPanel {
  flex: 1 50%;
  background-color: var(--surface-hover);
  border-left: 1px solid var(--border);
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 400px;
}

/* Trennstrich unterhalb der Status-Badges mit je 24px Abstand nach oben und unten */
.triggerRow {
  border-bottom: 1px solid var(--border);
  padding-bottom: 24px;
}

.triggerButton {
  min-width: 74px;
  height: 24px;
}

.infoGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 24px;
}

.infoCell {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.infoLabel {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Poppins, Helvetica, 'sans-serif';
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: var(--text-body);
}

.infoValue {
  font-size: 14px;
  color: var(--text);
}

.contentSection {
  padding: 16px 32px;
  border-top: 1px solid var(--border);
}

.tabs {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
}

.tab {
  background: none;
  border: none;
  padding: 0 0 6px 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-body);
  border-bottom: 2px solid transparent;
  cursor: pointer;
}

.tab.active {
  color: var(--primary);
  border-bottom-color: var(--primary);
}

.verlinkungen-bearbeiten-btn {
  display: flex;
  align-items: center;
  color: var(--primary);
  font-family: Poppins, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0px;
  text-decoration: none;
}

.verlinkungen-bearbeiten-btn:hover {
  color: #1086ff;
  text-decoration: none;
}

/* Platzhalter-Box für leere Tab-Inhalte */
.emptyBox {
  border: 1px dashed #d1d3e0;
  border-radius: 8px;
  background-color: #f8fafc;
  padding: 24px;
  margin-top: 16px;
  text-align: center;
  font-size: 14px;
  color: var(--text-muted);
}
:deep(.tooltip) {
  opacity: 1 !important;
}
:deep(.tooltip-inner) {
  padding: 8px 12px;
}
:deep(.custom-tooltip) > .tooltip-inner {
  max-width: none;
}
:deep(.modal) .modal-xxl {
  max-width: min(1800px, 92vw);
  width: min(1800px, 92vw);
}

.aufgabenContainer {
  display: flex;
  flex-direction: column;
}

:deep(.modal-xxl .modal-content) {
  background-color: var(--surface);
  padding: 0;
  height: 95vh;
  max-height: 95vh;
  margin: 0;
  border-radius: var(--radius-lg);
  overflow: hidden;
}

:deep(.modal-xxl .modal-header) {
  height: 53px;
  padding: 16px 32px;
  background: linear-gradient(90deg, #eff6ff 0%, #dbeafe 60%, #ffffff 100%);
  border-bottom: 1px solid #0000001a;
  align-items: center;
}

:deep(.modal-xxl .modal-header .close) {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  color: var(--text-body);
  opacity: 1;
}

/* Der Header bringt sein X über den modal-header-close-Slot selbst mit,
   deshalb entfällt hier die Mask aus modal-base-styles */
:deep(.modal-xxl .modal-header .close)::before {
  content: none;
}

:deep(.modal-xxl .modal-body) {
  padding: 0;
  overflow: auto;
}

/* Lösch-Icon im Header, rechts neben dem Beobachter-Button */
.deleteIconButton {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: none;
  border: none;
  color: #d4183d;
  cursor: pointer;
  margin-left: 16px;
}

.deleteIconButton:hover {
  color: #b3142f;
}
:deep(.ck.ck-dropdown .ck-dropdown__arrow) {
  z-index: none;
}

/* Editor (Beschreibung & Kommentar): gemeinsamer Rahmen */
:deep(.toolbar),
:deep(.tiptap) {
  border: 0.8px solid var(--border);
}

:deep(.toolbar) {
  background-color: var(--surface-hover);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  margin-bottom: 2px;
}

:deep(.tiptap) {
  background: #f3f3f5;
  border-radius: 0 0 8px 8px;
  outline: none;
}

/* Blauer Rahmen nur bei Fokus, nicht bei gespeichertem Inhalt */
:deep(.tiptap.ProseMirror-focused) {
  border-color: var(--primary);
}

/* Kommentar-Bereich */
.commentsWrapper :deep(.card.card-custom) {
  border: none;
  box-shadow: none;
  background: transparent;
  margin-bottom: 0;
}

.commentsWrapper :deep(.card.card-custom > div) {
  padding: 0 !important;
}

.commentsWrapper :deep(.card.card-custom > .mb-4) {
  margin-bottom: 8px !important;
}

.commentsWrapper :deep(.card-label),
.commentsWrapper :deep(.d-flex.flex-column > .font-weight-bold.text-muted) {
  display: none;
}

.commentsWrapper :deep(#commentsList) {
  overflow-y: auto !important;
}

.commentsWrapper :deep(#comments-collapse > div) {
  display: flex;
  flex-direction: column;
}

.commentsWrapper :deep(#comments-collapse > div) > .btn,
.commentsWrapper :deep(#comments-collapse > div) > div:not(#editor-input-field) {
  align-self: flex-end;
}

.beschreibungSaveButton:disabled {
  cursor: default;
  pointer-events: all;
  opacity: 1;
  background-color: #e4e6ef;
  border-color: #e4e6ef;
  color: var(--text-subtle);
}

:deep(p.is-editor-empty:first-child::before) {
  color: #adadad !important;
}
</style>
